import { DOCUMENT } from "@angular/common";
import { InjectionToken, Provider } from "@angular/core";

import { AppNavItem, AppNavUrlItem } from "@lantern/app/models";

export interface AppAuthConfig {
  martAuth: boolean;
  appAuthCheck: boolean;
}

export interface AppConfigArgs {
  auth?: Partial<AppAuthConfig> & {
    forceMartAuthDomains?: string[];
    skipMartAuthDomains?: string[];
  };
  displayName: string;
  menu?: AppNavItem[];
  headerMenu?: AppNavUrlItem[]; // nested menus not yet supported in header
  name: string;
  isGlobalApp?: boolean;
  apiPath?: string;
  docsUrl?: string;
}

export class AppConfig implements AppConfigArgs {
  auth: AppAuthConfig;
  displayName: string;
  menu?: AppNavItem[];
  headerMenu?: AppNavUrlItem[]; // nested menus not yet supported in header
  name: string;
  isGlobalApp: boolean;
  apiPath: string;
  docsUrl: string;

  private window: Window;

  constructor(config?: AppConfigArgs, window?: Window) {
    if (config == null) {
      // TODO: better error
      throw new Error("app.config is not valid");
    }
    this.window = window!;
    this.displayName = config.displayName;
    this.name = config.name;
    this.menu = config.menu;
    this.headerMenu = config.headerMenu;
    this.isGlobalApp = !!config.isGlobalApp;
    this.apiPath = config.apiPath || "";
    this.docsUrl = config.docsUrl || "/docs";

    this.auth = Object.assign(
      {
        appAuthCheck: true
      },
      {
        ...config.auth,
        martAuth:
          config.auth == null || this.resolveMartAuthForHost(config.auth)
      }
    );
  }

  private resolveMartAuthForHost(
    auth: Exclude<AppConfigArgs["auth"], undefined>
  ): boolean {
    if (this.forceMartAuthForHost(auth)) {
      return true;
    }
    if (this.skipMartAuthForHost(auth)) {
      return false;
    }
    return auth.martAuth != null ? auth.martAuth : true;
  }

  private skipMartAuthForHost(
    auth: Exclude<AppConfigArgs["auth"], undefined>
  ): boolean {
    return (
      auth.skipMartAuthDomains != null &&
      auth.skipMartAuthDomains.includes(this.window.location.hostname)
    );
  }

  private forceMartAuthForHost(
    auth: Exclude<AppConfigArgs["auth"], undefined>
  ): boolean {
    return (
      auth.forceMartAuthDomains != null &&
      auth.forceMartAuthDomains.includes(this.window.location.hostname)
    );
  }
}

export const APP_CONFIG_ARGS = new InjectionToken("APP_CONFIG_ARGS");

export function makeAppConfig(
  config: AppConfigArgs,
  document: Document
): AppConfig {
  return new AppConfig(config, document.defaultView!);
}

export function provideAppConfig(config: AppConfigArgs): Provider[] {
  return [
    {
      provide: APP_CONFIG_ARGS,
      useValue: config
    },
    {
      provide: AppConfig,
      useFactory: makeAppConfig,
      deps: [APP_CONFIG_ARGS, DOCUMENT]
    }
  ];
}
